/*
 * Copyright 2015 Mozilla Foundation
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { FilterType } from '../../../factories/base/SWFTags';
import { Bounds } from '@awayjs/graphics';
function parseBbox(stream) {
    stream.align();
    var bits = stream.readUb(5);
    var xMin = stream.readSb(bits);
    var xMax = stream.readSb(bits);
    var yMin = stream.readSb(bits);
    var yMax = stream.readSb(bits);
    stream.align();
    return { xMin: xMin, xMax: xMax, yMin: yMin, yMax: yMax };
}
export function parseRgb(stream) {
    return ((stream.readUi8() << 24) | (stream.readUi8() << 16) |
        (stream.readUi8() << 8) | 0xff) >>> 0;
}
function parseRgba(stream) {
    return (stream.readUi8() << 24) | (stream.readUi8() << 16) |
        (stream.readUi8() << 8) | stream.readUi8();
}
function parseArgb(stream) {
    return stream.readUi8() | (stream.readUi8() << 24) |
        (stream.readUi8() << 16) | (stream.readUi8() << 8);
}
function parseMatrix(stream) {
    var matrix = { a: 1, b: 0, c: 0, d: 1, tx: 0, ty: 0 };
    stream.align();
    var hasScale = stream.readUb(1);
    if (hasScale) {
        var bits = stream.readUb(5);
        matrix.a = stream.readFb(bits);
        matrix.d = stream.readFb(bits);
    }
    var hasRotate = stream.readUb(1);
    if (hasRotate) {
        var bits = stream.readUb(5);
        matrix.b = stream.readFb(bits);
        matrix.c = stream.readFb(bits);
    }
    var bits = stream.readUb(5);
    matrix.tx = stream.readSb(bits);
    matrix.ty = stream.readSb(bits);
    stream.align();
    return matrix;
}
function parseColorTransform(stream, hasAlpha) {
    var cxform = {
        redMultiplier: 0xff,
        greenMultiplier: 0xff,
        blueMultiplier: 0xff,
        alphaMultiplier: 0xff,
        redOffset: 0,
        greenOffset: 0,
        blueOffset: 0,
        alphaOffset: 0,
    };
    stream.align();
    var hasOffsets = stream.readUb(1);
    var hasMultipliers = stream.readUb(1);
    var bits = stream.readUb(4);
    if (hasMultipliers) {
        cxform.redMultiplier = stream.readSb(bits);
        cxform.greenMultiplier = stream.readSb(bits);
        cxform.blueMultiplier = stream.readSb(bits);
        if (hasAlpha) {
            cxform.alphaMultiplier = stream.readSb(bits);
        }
    }
    if (hasOffsets) {
        cxform.redOffset = stream.readSb(bits);
        cxform.greenOffset = stream.readSb(bits);
        cxform.blueOffset = stream.readSb(bits);
        if (hasAlpha) {
            cxform.alphaOffset = stream.readSb(bits);
        }
    }
    stream.align();
    return cxform;
}
function parsePlaceObjectTag(stream, swfVersion, tagCode, tagEnd) {
    var tag = { code: tagCode };
    tag.actionBlocksPrecedence = stream.pos;
    if (tagCode === 4 /* CODE_PLACE_OBJECT */) {
        tag.symbolId = stream.readUi16();
        tag.depth = stream.readUi16();
        tag.flags |= 4 /* HasMatrix */;
        tag.matrix = parseMatrix(stream);
        if (stream.pos < tagEnd) {
            tag.flags |= 8 /* HasColorTransform */;
            tag.cxform = parseColorTransform(stream, false);
        }
        return tag;
    }
    var flags = tag.flags = tagCode > 26 /* CODE_PLACE_OBJECT2 */ ?
        stream.readUi16() :
        stream.readUi8();
    tag.depth = stream.readUi16();
    if (flags & 2048 /* HasClassName */) {
        tag.className = stream.readString(-1);
    }
    if (flags & 2 /* HasCharacter */) {
        tag.symbolId = stream.readUi16();
    }
    if (flags & 4 /* HasMatrix */) {
        tag.matrix = parseMatrix(stream);
    }
    if (flags & 8 /* HasColorTransform */) {
        tag.cxform = parseColorTransform(stream, true);
    }
    if (flags & 16 /* HasRatio */) {
        tag.ratio = stream.readUi16();
    }
    if (flags & 32 /* HasName */) {
        tag.name = stream.readString(-1);
    }
    if (flags & 64 /* HasClipDepth */) {
        tag.clipDepth = stream.readUi16();
    }
    if (flags & 256 /* HasFilterList */) {
        var count = stream.readUi8();
        var filters = tag.filters = [];
        while (count--) {
            filters.push(parseFilter(stream));
        }
    }
    if (flags & 512 /* HasBlendMode */) {
        tag.blendMode = stream.readUi8();
    }
    if (flags & 1024 /* HasCacheAsBitmap */) {
        tag.bmpCache = stream.readUi8();
    }
    if (flags & 8192 /* HasVisible */) {
        tag.visibility = stream.readBool();
    }
    if (flags & 16384 /* OpaqueBackground */) {
        tag.backgroundColor = parseArgb(stream);
    }
    if (flags & 128 /* HasClipActions */) {
        var reserved = stream.readUi16();
        var allFlags = swfVersion >= 6 ? stream.readUi32() : stream.readUi16();
        var allEvents = tag.events = [];
        var events = void 0;
        while (events = parseEvents(stream, swfVersion)) {
            if (stream.pos > tagEnd) {
                console.log('PlaceObject handler attempted to read clip events beyond tag end');
                stream.pos = tagEnd;
                break;
            }
            allEvents.push(events);
        }
    }
    return tag;
}
function parseEvents(stream, swfVersion) {
    var flags = swfVersion >= 6 ? stream.readUi32() : stream.readUi16();
    if (!flags) {
        // `true` means this is the EndOfEvents marker.
        return null;
    }
    var events = {};
    // The Construct event is only allowed in 7+. It can't be set in < 6, so mask it out for 6.
    if (swfVersion === 6) {
        flags = flags & ~262144 /* Construct */;
    }
    events.flags = flags;
    var length = stream.readUi32();
    if (flags & 131072 /* KeyPress */) {
        events.keyCode = stream.readUi8();
        length--;
    }
    var end = stream.pos + length;
    events.actionsBlock = stream.bytes.subarray(stream.pos, end);
    stream.pos = end;
    return events;
}
function parseFilter(stream) {
    var filter = {};
    var type = filter.type = stream.readUi8();
    switch (type) {
        case FilterType.DROPSHADOW:
        case FilterType.GLOW:
        case FilterType.BEVEL:
        case FilterType.GRADIENTGLOW:
        case FilterType.GRADIENTBEVEL:
            var glow = filter;
            var count;
            if (type === FilterType.GRADIENTGLOW || type === FilterType.GRADIENTBEVEL) {
                count = stream.readUi8();
            }
            else {
                count = type === FilterType.BEVEL ? 2 : 1;
            }
            var colors = glow.colors = [];
            var i = count;
            while (i--) {
                colors.push(parseRgba(stream));
            }
            if (type === FilterType.GRADIENTGLOW || type === FilterType.GRADIENTBEVEL) {
                var ratios = glow.ratios = [];
                var i = count;
                while (i--) {
                    ratios.push(stream.readUi8());
                }
            }
            glow.blurX = stream.readFixed();
            glow.blurY = stream.readFixed();
            if (type !== FilterType.GLOW) {
                glow.angle = stream.readFixed();
                glow.distance = stream.readFixed();
            }
            glow.strength = stream.readFixed8();
            glow.inner = !!stream.readUb(1);
            glow.knockout = !!stream.readUb(1);
            glow.compositeSource = !!stream.readUb(1);
            if (type === FilterType.BEVEL || type === FilterType.GRADIENTGLOW || type === FilterType.GRADIENTBEVEL) {
                glow.onTop = !!stream.readUb(1);
                glow.quality = stream.readUb(4);
            }
            else {
                glow.quality = stream.readUb(5);
            }
            return glow;
        case FilterType.BLUR:
            var blur = filter;
            blur.blurX = stream.readFixed();
            blur.blurY = stream.readFixed();
            blur.quality = stream.readUb(5);
            stream.readUb(3);
            return blur;
        case FilterType.CONVOLUTION:
            var conv = filter;
            var matrixX = conv.matrixX = stream.readUi8();
            var matrixY = conv.matrixY = stream.readUi8();
            conv.divisor = stream.readFloat();
            conv.bias = stream.readFloat();
            var matrix = conv.matrix = [];
            var i = matrixX * matrixY;
            while (i--) {
                matrix.push(stream.readFloat());
            }
            conv.color = parseRgba(stream);
            var reserved = stream.readUb(6);
            conv.clamp = !!stream.readUb(1);
            conv.preserveAlpha = !!stream.readUb(1);
            return conv;
        case FilterType.COLORMATRIX:
            var cm = filter;
            var matrix = cm.matrix = [];
            var i = 20;
            while (i--) {
                matrix.push(stream.readFloat());
            }
            return cm;
        default:
    }
    return filter;
}
function parseRemoveObjectTag(stream, swfVersion, tagCode) {
    var tag = { code: tagCode };
    if (tagCode === 5 /* CODE_REMOVE_OBJECT */) {
        tag.depth = stream.readUi16();
        tag.symbolId = stream.readUi16();
    }
    else {
        tag.depth = stream.readUi16();
    }
    return tag;
}
export function parseDefineImageTag(stream, swfVersion, tagCode, tagEnd, jpegTables) {
    var tag = { code: tagCode };
    tag.id = stream.readUi16();
    var imgData;
    if (tagCode > 21 /* CODE_DEFINE_BITS_JPEG2 */) {
        var alphaDataOffset = stream.readUi32();
        if (tagCode === 90 /* CODE_DEFINE_BITS_JPEG4 */) {
            tag.deblock = stream.readFixed8();
        }
        alphaDataOffset += stream.pos;
        imgData = tag.imgData = stream.bytes.subarray(stream.pos, alphaDataOffset);
        tag.alphaData = stream.bytes.subarray(alphaDataOffset, tagEnd);
        stream.pos = tagEnd;
    }
    else {
        imgData = tag.imgData = stream.bytes.subarray(stream.pos, tagEnd);
        stream.pos = tagEnd;
    }
    switch (imgData[0] << 8 | imgData[1]) {
        case 65496:
        case 65497:
            tag.mimeType = 'image/jpeg';
            break;
        case 35152:
            tag.mimeType = 'image/png';
            break;
        case 18249:
            tag.mimeType = 'image/gif';
            break;
        default:
            tag.mimeType = 'application/octet-stream';
    }
    if (tagCode === 6 /* CODE_DEFINE_BITS */) {
        tag.jpegTables = { data: jpegTables };
    }
    return tag;
}
function parseDefineButtonTag(stream, swfVersion, tagCode, tagEnd) {
    var tag = { code: tagCode };
    tag.id = stream.readUi16();
    var characters = tag.characters = [];
    var character;
    if (tagCode == 7 /* CODE_DEFINE_BUTTON */) {
        while (character = parseButtonCharacter(stream, swfVersion, tagCode)) {
            characters.push(character);
        }
        tag.actionsData = stream.bytes.subarray(stream.pos, tagEnd);
        stream.pos = tagEnd;
    }
    else {
        var trackFlags = stream.readUi8();
        tag.trackAsMenu = !!(trackFlags >> 7 & 1);
        var actionOffset = stream.readUi16();
        while (character = parseButtonCharacter(stream, swfVersion, tagCode)) {
            characters.push(character);
        }
        if (actionOffset) {
            var buttonActions = tag.buttonActions = [];
            while (stream.pos < tagEnd) {
                var action = parseButtonCondAction(stream, tagEnd);
                // Ignore actions that exceed the tag length.
                if (stream.pos > tagEnd) {
                    break;
                }
                buttonActions.push(action);
            }
            stream.pos = tagEnd;
        }
    }
    return tag;
}
function parseButtonCharacter(stream, swfVersion, tagCode) {
    var flags = stream.readUi8();
    if (!flags) {
        return null;
    }
    var character = {};
    if (swfVersion < 8) {
        // Clear HasBlendMode and HasFilterList flags.
        flags &= ~(512 /* HasBlendMode */ | 256 /* HasFilterList */);
    }
    character.flags = flags;
    character.symbolId = stream.readUi16();
    character.depth = stream.readUi16();
    character.matrix = parseMatrix(stream);
    if (tagCode === 34 /* CODE_DEFINE_BUTTON2 */) {
        character.cxform = parseColorTransform(stream, true);
    }
    if (character.flags & 16 /* HasFilterList */) {
        var count = stream.readUi8();
        var filters = character.filters = [];
        var i = count;
        while (i--) {
            filters.push(parseFilter(stream));
        }
    }
    if (character.flags & 32 /* HasBlendMode */) {
        character.blendMode = stream.readUi8();
    }
    return character;
}
function parseButtonCondAction(stream, tagEnd) {
    var start = stream.pos;
    var tagSize = stream.readUi16();
    // If no tagSize is given, read to the tag's end.
    var end = tagSize ? start + tagSize : tagEnd;
    var conditions = stream.readUi16();
    stream.pos = end;
    return {
        // The 7 upper bits hold a key code the button should respond to.
        keyCode: (conditions & 0xfe00) >> 9,
        // The lower 9 bits hold state transition flags. See the enum in AVM1Button for details.
        stateTransitionFlags: conditions & 0x1ff,
        // If no tagSize is given, pass `0` to readBinary.
        actionsData: stream.bytes.subarray(start + 4, end)
    };
}
function parseDefineBinaryDataTag(stream, swfVersion, tagCode, tagEnd) {
    var tag = { code: tagCode };
    tag.id = stream.readUi16();
    stream.pos += 4; // Reserved
    tag.data = stream.bytes.subarray(stream.pos, tagEnd);
    stream.pos = tagEnd;
    return tag;
}
export function parseDefineFontTag(stream, swfVersion, tagCode) {
    var tag = { code: tagCode };
    tag.id = stream.readUi16();
    var firstOffset = stream.readUi16();
    var glyphCount = firstOffset / 2;
    var restOffsets = [];
    var i = glyphCount - 1;
    while (i--) {
        restOffsets.push(stream.readUi16());
    }
    tag.offsets = [firstOffset].concat(restOffsets);
    var glyphs = tag.glyphs = [];
    i = glyphCount;
    while (i--) {
        glyphs.push(parseGlyph(stream, swfVersion, tagCode));
    }
    return tag;
}
function parseGlyph(stream, swfVersion, tagCode) {
    stream.align();
    var fillBits = stream.readUb(4);
    var lineBits = stream.readUb(4);
    return parseShapeRecords(stream, swfVersion, tagCode, false, fillBits, lineBits, false);
}
function parseDefineTextTag(stream, swfVersion, tagCode) {
    var tag = { code: tagCode };
    tag.id = stream.readUi16();
    tag.bbox = parseBbox(stream);
    tag.matrix = parseMatrix(stream);
    var glyphBits = stream.readUi8();
    var advanceBits = stream.readUi8();
    var records = tag.records = [];
    var record;
    while (record = parseTextRecord(stream, swfVersion, tagCode, glyphBits, advanceBits)) {
        records.push(record);
    }
    return tag;
}
function parseTextRecord(stream, swfVersion, tagCode, glyphBits, advanceBits) {
    stream.align();
    var flags = stream.readUb(8);
    if (!flags) {
        return null;
    }
    var record = {};
    record.flags = flags;
    if (flags & 8 /* HasFont */) {
        record.fontId = stream.readUi16();
    }
    if (flags & 4 /* HasColor */) {
        record.color = tagCode === 33 /* CODE_DEFINE_TEXT2 */ ?
            parseRgba(stream) :
            parseRgb(stream);
    }
    if (flags & 1 /* HasMoveX */) {
        record.moveX = stream.readSi16();
    }
    if (flags & 2 /* HasMoveY */) {
        record.moveY = stream.readSi16();
    }
    if (flags & 8 /* HasFont */) {
        record.fontHeight = stream.readUi16();
    }
    var glyphCount = stream.readUi8();
    if (swfVersion <= 6) {
        //glyphCount = glyphCount; // & 0x7f ???;
    }
    var entries = record.entries = [];
    var i = glyphCount;
    while (i--) {
        entries.push({
            glyphIndex: stream.readUb(glyphBits),
            advance: stream.readSb(advanceBits)
        });
    }
    return record;
}
function parseDefineSoundTag(stream, swfVersion, tagCode, tagEnd) {
    var tag = { code: tagCode };
    tag.id = stream.readUi16();
    var soundFlags = stream.readUi8();
    tag.soundFormat = soundFlags >> 4 & 15;
    tag.soundRate = soundFlags >> 2 & 3;
    tag.soundSize = soundFlags >> 1 & 1;
    tag.soundType = soundFlags & 1;
    tag.samplesCount = stream.readUi32();
    tag.soundData = stream.bytes.subarray(stream.pos, tagEnd);
    stream.pos = tagEnd;
    return tag;
}
function parseStartSoundTag(stream, swfVersion, tagCode) {
    var tag = { code: tagCode };
    if (tagCode == 15 /* CODE_START_SOUND */) {
        tag.soundId = stream.readUi16();
    }
    if (tagCode == 89 /* CODE_START_SOUND2 */) {
        tag.soundClassName = stream.readString(-1);
    }
    tag.soundInfo = parseSoundInfo(stream);
    return tag;
}
export function parseSoundInfo(stream) {
    var info = {};
    var flags = info.flags = stream.readUi8();
    if (flags & 1 /* HasInPoint */) {
        info.inPoint = stream.readUi32();
    }
    if (flags & 2 /* HasOutPoint */) {
        info.outPoint = stream.readUi32();
    }
    if (flags & 4 /* HasLoops */) {
        info.loopCount = stream.readUi16();
    }
    if (flags & 8 /* HasEnvelope */) {
        var envelopeCount = stream.readUi8();
        var envelopes = info.envelopes = [];
        var i = envelopeCount;
        while (i--) {
            envelopes.push({
                pos44: stream.readUi32(),
                volumeLeft: stream.readUi16(),
                volumeRight: stream.readUi16()
            });
        }
    }
    return info;
}
export function parseSoundStreamHeadTag(stream, tagEnd) {
    var tag = {};
    var playbackFlags = stream.readUi8();
    tag.playbackRate = playbackFlags >> 2 & 3;
    tag.playbackSize = playbackFlags >> 1 & 1;
    tag.playbackType = playbackFlags & 1;
    var streamFlags = stream.readUi8();
    var streamCompression = tag.streamCompression = streamFlags >> 4 & 15;
    tag.streamRate = streamFlags >> 2 & 3;
    tag.streamSize = (streamFlags >> 1 & 1) == 0 ? 8 : 16;
    tag.streamType = streamFlags & 1;
    tag.samplesCount = stream.readUi16();
    if (streamCompression == 2 && tagEnd - stream.pos >= 2) {
        tag.latencySeek = stream.readSi16();
    }
    return tag;
}
export function parseDefineBitmapTag(stream, swfVersion, tagCode, tagEnd) {
    var tag = { code: tagCode };
    tag.id = stream.readUi16();
    var format = tag.format = stream.readUi8();
    tag.width = stream.readUi16();
    tag.height = stream.readUi16();
    tag.hasAlpha = tagCode === 36 /* CODE_DEFINE_BITS_LOSSLESS2 */;
    if (format === 3) {
        tag.colorTableSize = stream.readUi8();
    }
    tag.bmpData = stream.bytes.subarray(stream.pos, tagEnd);
    stream.pos = tagEnd;
    return tag;
}
function parseDefineEditTextTag(stream, swfVersion, tagCode) {
    var tag = { code: tagCode };
    tag.id = stream.readUi16();
    tag.bbox = parseBbox(stream);
    var flags = tag.flags = stream.readUi16();
    if (flags & 1 /* HasFont */) {
        tag.fontId = stream.readUi16();
    }
    if (flags & 32768 /* HasFontClass */) {
        tag.fontClass = stream.readString(-1);
    }
    if (flags & 1 /* HasFont */) {
        tag.fontHeight = stream.readUi16();
    }
    if (flags & 4 /* HasColor */) {
        tag.color = parseRgba(stream);
    }
    if (flags & 2 /* HasMaxLength */) {
        tag.maxLength = stream.readUi16();
    }
    if (flags & 8192 /* HasLayout */) {
        tag.align = stream.readUi8();
        tag.leftMargin = stream.readUi16();
        tag.rightMargin = stream.readUi16();
        tag.indent = stream.readSi16();
        tag.leading = stream.readSi16();
    }
    tag.variableName = stream.readString(-1);
    if (flags & 128 /* HasText */) {
        tag.initialText = stream.readString(-1);
    }
    return tag;
}
export function parseDefineFont2Tag(stream, swfVersion, tagCode, tagEnd) {
    var tag = { code: tagCode };
    tag.id = stream.readUi16();
    var flags = tag.flags = stream.readUi8();
    var wide = !!(flags & 4 /* WideOrHasFontData */);
    if (swfVersion > 5) {
        tag.language = stream.readUi8();
    }
    else {
        // Clear ShiftJis flag.
        flags = tag.flags = flags & ~64 /* ShiftJis */;
        // Skip reserved byte.
        stream.pos += 1;
        tag.language = 0;
    }
    var nameLength = stream.readUi8();
    tag.name = stream.readString(nameLength);
    if (tagCode === 75 /* CODE_DEFINE_FONT3 */) {
        tag.resolution = 20;
    }
    var glyphCount = stream.readUi16();
    // The SWF format docs doesn't say that, but the DefineFont{2,3} tag ends here for device fonts.
    if (glyphCount === 0) {
        return tag;
    }
    var startpos = stream.pos;
    var offsets = tag.offsets = [];
    var i = glyphCount;
    if (flags & 8 /* WideOffset */) {
        while (i--) {
            offsets.push(stream.readUi32());
        }
        tag.mapOffset = stream.readUi32();
    }
    else {
        while (i--) {
            offsets.push(stream.readUi16());
        }
        tag.mapOffset = stream.readUi16();
    }
    var glyphs = tag.glyphs = [];
    i = glyphCount;
    while (i--) {
        var dist = tag.offsets[glyphCount - i] + startpos - stream.pos;
        // when just one byte difference between two offsets, just read that and insert an empty glyph.
        if (dist === 1) {
            stream.pos += 1;
            glyphs.push([]);
            continue;
        }
        glyphs.push(parseGlyph(stream, swfVersion, tagCode));
    }
    var codes = tag.codes = [];
    i = glyphCount;
    while (i--) {
        codes.push(wide ? stream.readUi16() : stream.readUi8());
    }
    if (flags & 128 /* HasLayout */) {
        tag.ascent = stream.readUi16();
        tag.descent = stream.readUi16();
        tag.leading = stream.readSi16();
        var advance = tag.advance = [];
        i = glyphCount;
        while (i--) {
            advance.push(stream.readSi16());
        }
        var bbox = tag.bbox = [];
        i = glyphCount;
        while (i--) {
            bbox.push(parseBbox(stream));
        }
        var kerningCount = stream.readUi16();
        var kernings = tag.kerning = [];
        i = kerningCount;
        // DefineFont2 tags tend to have a wrong kerning count so we have to make sure here that there is enough unread
        // data remaining before parsing the next kerning record. If not, we have to bail out earlier in the following
        // loop to avoid reading out of bound.
        while (i-- && tagEnd - stream.pos >= (wide ? 4 : 2) + 2) {
            kernings.push(parseKerning(stream, wide));
        }
    }
    return tag;
}
function parseKerning(stream, wide) {
    var kerning = {};
    if (wide) {
        kerning.code1 = stream.readUi16();
        kerning.code2 = stream.readUi16();
    }
    else {
        kerning.code1 = stream.readUi8();
        kerning.code2 = stream.readUi8();
    }
    kerning.adjustment = stream.readUi16();
    return kerning;
}
export function parseDefineFont4Tag(stream, swfVersion, tagCode, tagEnd) {
    var tag = { code: tagCode };
    tag.id = stream.readUi16();
    var flags = tag.flags = stream.readUi8();
    tag.name = stream.readString(-1);
    if (flags & 4 /* WideOrHasFontData */) {
        tag.data = stream.bytes.slice(stream.pos, tagEnd);
        stream.pos = tagEnd;
    }
    return tag;
}
function parseDefineScalingGridTag(stream, swfVersion, tagCode) {
    var tag = { code: tagCode };
    tag.symbolId = stream.readUi16();
    tag.splitter = parseBbox(stream);
    return tag;
}
/*
const sortScenes = function (a:Scene, b:Scene){
    return a.offset-b.offset;
}
*/
export function parseDefineSceneTag(stream, tagCode) {
    var tag = { code: tagCode };
    var sceneCount = stream.readEncodedU32();
    var scenes = tag.scenes = [];
    var i = sceneCount;
    while (i--) {
        scenes.push({
            offset: stream.readEncodedU32(),
            name: stream.readString(-1),
            labels: [],
            numFrames: -1,
        });
        if (scenes.length > 1) {
            scenes[scenes.length - 2].numFrames =
                scenes[scenes.length - 1].offset - scenes[scenes.length - 2].offset;
        }
    }
    // @todo: do we need to sort scenes, or are they coming in in order of offset ?
    //scenes.sort(sortScenes);
    var labelCount = stream.readEncodedU32();
    var labels = tag.labels = [];
    i = labelCount;
    while (i--) {
        var label = {
            frame: stream.readEncodedU32(),
            name: stream.readString(-1)
        };
        labels.push(label);
        var currentScene = scenes[0];
        for (var s = 1; s < scenes.length; s++) {
            var scene = scenes[s];
            if (scene.offset > label.frame)
                break;
            currentScene = scene;
        }
        currentScene.labels.push(label);
    }
    return tag;
}
function parseDefineShapeTagLazy(stream, tag, swfVersion) {
    var canHaveStrokes = tag.code === 83 /* CODE_DEFINE_SHAPE4 */ ||
        tag.code === 84 /* CODE_DEFINE_MORPH_SHAPE2 */;
    var isMorph = !!(tag.flags & 8 /* IsMorph */);
    tag.fillStyles = parseFillStyles(stream, swfVersion, tag.code, isMorph);
    tag.lineStyles = parseLineStyles(stream, swfVersion, tag.code, isMorph, canHaveStrokes);
    stream.align();
    var fillBits = stream.readUb(4);
    var lineBits = stream.readUb(4);
    tag.records = parseShapeRecords(stream, swfVersion, tag.code, isMorph, fillBits, lineBits, canHaveStrokes);
    if (isMorph) {
        stream.align();
        fillBits = stream.readUb(4);
        lineBits = stream.readUb(4);
        tag.recordsMorph = parseShapeRecords(stream, swfVersion, tag.code, isMorph, fillBits, lineBits, canHaveStrokes);
    }
    return tag;
}
function parseDefineShapeTag(stream, swfVersion, tagCode, tagEnd) {
    var tag = { code: tagCode, needParse: true };
    var flags = 0;
    tag.id = stream.readUi16();
    tag.lineBounds = parseBbox(stream);
    var isMorph = tagCode === 46 /* CODE_DEFINE_MORPH_SHAPE */ ||
        tagCode === 84 /* CODE_DEFINE_MORPH_SHAPE2 */;
    if (isMorph) {
        flags |= 8 /* IsMorph */;
        tag.lineBoundsMorph = parseBbox(stream);
    }
    var canHaveStrokes = (tagCode === 83 /* CODE_DEFINE_SHAPE4 */
        || tagCode === 84 /* CODE_DEFINE_MORPH_SHAPE2 */);
    if (canHaveStrokes) {
        tag.fillBounds = parseBbox(stream);
        if (isMorph) {
            tag.fillBoundsMorph = parseBbox(stream);
        }
        flags |= stream.readUi8() & 0x07;
    }
    tag.flags = flags;
    if (isMorph) {
        stream.pos += 4;
    }
    var sub = stream.substream(stream.pos, stream.end);
    tag.lazyParser = function () {
        tag.needParse = false;
        tag.lazyParser = function () { return tag; };
        var begin = performance.now();
        parseDefineShapeTagLazy(sub, tag, swfVersion);
        tag.parsingTime = performance.now() - begin;
        return tag;
    };
    stream.pos = tagEnd;
    return tag;
}
function parseShapeRecords(stream, swfVersion, tagCode, isMorph, fillBits, lineBits, hasStrokes) {
    var records = new Array(10);
    var index = 0;
    var bits;
    var flags;
    do {
        var record = {};
        var type = record.type = stream.readUb(1);
        flags = stream.readUb(5);
        if (!(type || flags)) {
            break;
        }
        if (type) {
            bits = (flags & 0x0f) + 2;
            flags = (flags & 0xf0) << 1;
            if (flags & 32 /* IsStraight */) {
                var isGeneral = record.isGeneral = stream.readUb(1);
                if (isGeneral) {
                    flags |= 64 /* IsGeneral */;
                    record.deltaX = stream.readSb(bits);
                    record.deltaY = stream.readSb(bits);
                }
                else {
                    var isVertical = record.isVertical = stream.readUb(1);
                    if (isVertical) {
                        flags |= 128 /* IsVertical */;
                        record.deltaY = stream.readSb(bits);
                    }
                    else {
                        record.deltaX = stream.readSb(bits);
                    }
                }
            }
            else {
                record.controlDeltaX = stream.readSb(bits);
                record.controlDeltaY = stream.readSb(bits);
                record.anchorDeltaX = stream.readSb(bits);
                record.anchorDeltaY = stream.readSb(bits);
            }
        }
        else {
            if (tagCode <= 2 /* CODE_DEFINE_SHAPE */) {
                // Clear HasNewStyles flag.
                flags &= ~16 /* HasNewStyles */;
            }
            if (flags & 1 /* Move */) {
                bits = stream.readUb(5);
                record.moveX = stream.readSb(bits);
                record.moveY = stream.readSb(bits);
            }
            if (flags & 2 /* HasFillStyle0 */) {
                record.fillStyle0 = stream.readUb(fillBits);
            }
            if (flags & 4 /* HasFillStyle1 */) {
                record.fillStyle1 = stream.readUb(fillBits);
            }
            if (flags & 8 /* HasLineStyle */) {
                record.lineStyle = stream.readUb(lineBits);
            }
            if (flags & 16 /* HasNewStyles */) {
                record.fillStyles = parseFillStyles(stream, swfVersion, tagCode, isMorph);
                record.lineStyles = parseLineStyles(stream, swfVersion, tagCode, isMorph, hasStrokes);
                stream.align();
                fillBits = stream.readUb(4);
                lineBits = stream.readUb(4);
            }
        }
        record.flags = flags;
        records[index++] = record;
        // eslint-disable-next-line no-constant-condition
    } while (1);
    if (records.length > index) {
        records.length = index;
    }
    return records;
}
function parseFillStyles(stream, swfVersion, tagCode, isMorph) {
    var count = stream.readUi8();
    if (tagCode > 2 /* CODE_DEFINE_SHAPE */ && count === 255) {
        count = stream.readUi16();
    }
    var styles = [];
    var i = count;
    while (i--) {
        styles.push(parseFillStyle(stream, swfVersion, tagCode, isMorph));
    }
    return styles;
}
function parseFillStyle(stream, swfVersion, tagCode, isMorph) {
    var style = {};
    var type = style.type = stream.readUi8();
    switch (type) {
        case 0:
            var solid = style;
            solid.color = tagCode > 22 /* CODE_DEFINE_SHAPE2 */ || isMorph ?
                parseRgba(stream) :
                parseRgb(stream);
            if (isMorph) {
                solid.colorMorph = parseRgba(stream);
            }
            return solid;
        case 16:
        case 18:
        case 19:
            var gradient = style;
            gradient.matrix = parseMatrix(stream);
            if (isMorph) {
                gradient.matrixMorph = parseMatrix(stream);
            }
            //console.log("GradientFill", tagCode);
            if (tagCode === 83 /* CODE_DEFINE_SHAPE4 */) {
                gradient.spreadMode = stream.readUb(2);
                gradient.interpolationMode = stream.readUb(2);
                //console.log("gradient.spreadMode", gradient.spreadMode);
                //console.log("gradient.interpolationMode", gradient.interpolationMode);
            }
            else {
                stream.readUb(4);
            }
            var count = stream.readUb(4);
            var records = gradient.records = [];
            var j = count;
            while (j--) {
                records.push(parseGradientRecord(stream, tagCode, isMorph));
            }
            if (type === 19) {
                gradient.focalPoint = stream.readSi16();
                if (isMorph) {
                    gradient.focalPointMorph = stream.readSi16();
                }
            }
            return gradient;
        case 64:
        case 65:
        case 66:
        case 67:
            var pattern = style;
            pattern.bitmapId = stream.readUi16();
            pattern.condition = type === 64 || type === 67;
            pattern.matrix = parseMatrix(stream);
            if (isMorph) {
                pattern.matrixMorph = parseMatrix(stream);
            }
            return pattern;
    }
    return style;
}
function parseGradientRecord(stream, tagCode, isMorph) {
    var record = {};
    record.ratio = stream.readUi8();
    if (tagCode > 22 /* CODE_DEFINE_SHAPE2 */) {
        record.color = parseRgba(stream);
    }
    else {
        record.color = parseRgb(stream);
    }
    if (isMorph) {
        record.ratioMorph = stream.readUi8();
        record.colorMorph = parseRgba(stream);
    }
    return record;
}
function parseLineStyles(stream, swfVersion, tagCode, isMorph, hasStrokes) {
    var count = stream.readUi8();
    if (tagCode > 2 /* CODE_DEFINE_SHAPE */ && count === 255) {
        count = stream.readUi16();
    }
    var styles = [];
    var i = count;
    while (i--) {
        var style = {};
        style.width = stream.readUi16();
        if (isMorph) {
            style.widthMorph = stream.readUi16();
        }
        if (hasStrokes) {
            stream.align();
            style.startCapsStyle = stream.readUb(2);
            var jointStyle = style.jointStyle = stream.readUb(2);
            var hasFill = style.hasFill = stream.readUb(1);
            style.noHscale = !!stream.readUb(1);
            style.noVscale = !!stream.readUb(1);
            // console.log("style.noHscale", style.noHscale, "style.noVscale", style.noVscale);
            style.pixelHinting = !!stream.readUb(1);
            stream.readUb(5);
            style.noClose = !!stream.readUb(1);
            style.endCapsStyle = stream.readUb(2);
            if (jointStyle === 2) {
                style.miterLimitFactor = stream.readFixed8();
            }
            if (hasFill) {
                style.fillStyle = parseFillStyle(stream, swfVersion, tagCode, isMorph);
            }
            else {
                style.color = parseRgba(stream);
                if (isMorph) {
                    style.colorMorph = parseRgba(stream);
                }
            }
        }
        else {
            //console.log("No strokes");
            if (tagCode > 22 /* CODE_DEFINE_SHAPE2 */) {
                style.color = parseRgba(stream);
            }
            else {
                style.color = parseRgb(stream);
            }
            if (isMorph) {
                style.colorMorph = parseRgba(stream);
            }
        }
        styles.push(style);
    }
    return styles;
}
function parseDefineVideoStreamTag(stream, swfVersion, tagCode) {
    var tag = { code: tagCode };
    tag.id = stream.readUi16();
    tag.numFrames = stream.readUi16();
    tag.width = stream.readUi16();
    tag.height = stream.readUi16();
    stream.readUb(4);
    tag.deblocking = stream.readUb(3);
    tag.smoothing = !!stream.readUb(1);
    tag.codecId = stream.readUi8();
    return tag;
}
function parseVideoFrameTag(stream, swfVersion, tagCode, tagEnd) {
    var tag = { code: tagCode };
    tag.streamId = stream.readUi16();
    tag.frameNum = stream.readUi16();
    tag.videoData = stream.bytes.subarray(stream.pos, tagEnd);
    stream.pos = tagEnd;
    return tag;
}
export var tagHandlers = {
    /* End */ 0: undefined,
    /* ShowFrame */ 1: undefined,
    /* DefineShape */ 2: parseDefineShapeTag,
    /* PlaceObject */ 4: parsePlaceObjectTag,
    /* RemoveObject */ 5: parseRemoveObjectTag,
    /* DefineBits */ 6: parseDefineImageTag,
    /* DefineButton */ 7: parseDefineButtonTag,
    /* JPEGTables */ 8: undefined,
    /* SetBackgroundColor */ 9: undefined,
    /* DefineFont */ 10: parseDefineFontTag,
    /* DefineText */ 11: parseDefineTextTag,
    /* DoAction */ 12: undefined,
    /* DefineFontInfo */ 13: undefined,
    /* DefineSound */ 14: parseDefineSoundTag,
    /* StartSound */ 15: parseStartSoundTag,
    /* DefineButtonSound */ 17: undefined,
    /* SoundStreamHead */ 18: undefined,
    /* SoundStreamBlock */ 19: undefined,
    /* DefineBitsLossless */ 20: parseDefineBitmapTag,
    /* DefineBitsJPEG2 */ 21: parseDefineImageTag,
    /* DefineShape2 */ 22: parseDefineShapeTag,
    /* DefineButtonCxform */ 23: undefined,
    /* Protect */ 24: undefined,
    /* PlaceObject2 */ 26: parsePlaceObjectTag,
    /* RemoveObject2 */ 28: parseRemoveObjectTag,
    /* DefineShape3 */ 32: parseDefineShapeTag,
    /* DefineText2 */ 33: parseDefineTextTag,
    /* DefineButton2 */ 34: parseDefineButtonTag,
    /* DefineBitsJPEG3 */ 35: parseDefineImageTag,
    /* DefineBitsLossless2 */ 36: parseDefineBitmapTag,
    /* DefineEditText */ 37: parseDefineEditTextTag,
    /* DefineSprite */ 39: undefined,
    /* FrameLabel */ 43: undefined,
    /* SoundStreamHead2 */ 45: undefined,
    /* DefineMorphShape */ 46: parseDefineShapeTag,
    /* DefineFont2 */ 48: parseDefineFont2Tag,
    /* ExportAssets */ 56: undefined,
    /* ImportAssets */ 57: undefined,
    /* EnableDebugger */ 58: undefined,
    /* DoInitAction */ 59: undefined,
    /* DefineVideoStream */ 60: parseDefineVideoStreamTag,
    /* VideoFrame */ 61: parseVideoFrameTag,
    /* DefineFontInfo2 */ 62: undefined,
    /* EnableDebugger2 */ 64: undefined,
    /* ScriptLimits */ 65: undefined,
    /* SetTabIndex */ 66: undefined,
    /* FileAttributes */ 69: undefined,
    /* PlaceObject3 */ 70: parsePlaceObjectTag,
    /* ImportAssets2 */ 71: undefined,
    /* DoABC (undoc) */ 72: undefined,
    /* DefineFontAlignZones */ 73: undefined,
    /* CSMTextSettings */ 74: undefined,
    /* DefineFont3 */ 75: parseDefineFont2Tag,
    /* SymbolClass */ 76: undefined,
    /* Metadata */ 77: undefined,
    /* DefineScalingGrid */ 78: parseDefineScalingGridTag,
    /* DoABC */ 82: undefined,
    /* DefineShape4 */ 83: parseDefineShapeTag,
    /* DefineMorphShape2 */ 84: parseDefineShapeTag,
    /* DefineSceneAndFrameLabelData */ 86: parseDefineSceneTag,
    /* DefineBinaryData */ 87: parseDefineBinaryDataTag,
    /* DefineFontName */ 88: undefined,
    /* StartSound2 */ 89: parseStartSoundTag,
    /* DefineBitsJPEG4 */ 90: parseDefineImageTag,
    /* DefineFont4 */ 91: parseDefineFont4Tag
};
export function parseHeader(stream) {
    var bits = stream.readUb(5);
    var xMin = stream.readSb(bits);
    var xMax = stream.readSb(bits);
    var yMin = stream.readSb(bits);
    var yMax = stream.readSb(bits);
    stream.align();
    var frameRateFraction = stream.readUi8();
    var frameRate = stream.readUi8() + frameRateFraction / 256;
    var frameCount = stream.readUi16();
    return {
        frameRate: frameRate,
        frameCount: frameCount,
        bounds: new Bounds(xMin, yMin, xMax, yMax)
    };
}
